import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── HaircutBeauty — 가위 슬라이스 게임 (Fruit Ninja 스타일) ───
// 떨어지는 머리카락 이모지를 스와이프로 자르고, 빗·드라이어는 피한다.
// 머리카락 = +1 / 꽃·립스틱(보너스) = +3 / 빗·드라이어(나쁨) = -1 life
// 머리카락 5개 놓치거나 라이프 0이면 게임 오버.

const W = 360;
const H = 520;
const GRAV = 0.32;
const HIT_R = 26;        // 객체 충돌 반경
const SLICE_TAIL = 8;    // 스와이프 잔상 길이

const HAIRS = ['💁‍♀️','💁‍♂️','🦱','🦰','🦳'];   // good (+1)
const BONUS = ['🌸','💐','💝'];                  // bonus (+3)
const BADS  = ['🪮','💄','🪒'];                  // bad (-life)
const BEST_KEY = 'ddabak_egg_best_beauty';

// 가위 커서 — 인라인 SVG에 ✂️ 이모지 임베딩. hotspot은 가위 중심(16,16).
const SCISSORS_CURSOR = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><text x='16' y='24' font-size='26' text-anchor='middle'>%E2%9C%82%EF%B8%8F</text></svg>") 16 16, crosshair`;

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function drawEmoji(ctx, emoji, x, y, size = 38, rot = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 0, 0);
  ctx.restore();
}

// 두 선분 간 거리 (slash segment ↔ item center 검사용)
function distPointToSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export default function HaircutBeauty({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);
  const rafRef    = useRef(null);
  const [phase, setPhase] = useState('idle');   // idle | playing | dead
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [combo, setCombo] = useState(0);
  const [best,  setBest]  = useState(
    () => parseInt(localStorage.getItem(BEST_KEY) || '0')
  );

  const initState = useCallback(() => ({
    items: [],
    slash: [],          // {x, y, t} — 최근 스와이프 점
    particles: [],
    frame: 0,
    score: 0,
    lives: 3,
    missed: 0,
    combo: 0,
    comboTimer: 0,
    spawnTimer: 0,
  }), []);

  // 객체 던지기 (아래에서 위로)
  function spawnItem(s) {
    const r = Math.random();
    let type, emoji;
    if (r < 0.7)      { type = 'good';  emoji = pick(HAIRS); }
    else if (r < 0.85){ type = 'bonus'; emoji = pick(BONUS); }
    else              { type = 'bad';   emoji = pick(BADS); }

    s.items.push({
      id: Math.random(),
      x: 60 + Math.random() * (W - 120),
      y: H + 30,
      vx: (Math.random() - 0.5) * 4,
      vy: -10 - Math.random() * 3.5,
      rot: 0,
      vrot: (Math.random() - 0.5) * 0.18,
      type, emoji,
      sliced: false,
    });
  }

  function spawnParticles(arr, x, y, color, text) {
    for (let i = 0; i < 6; i++) {
      arr.push({
        x, y,
        vx: (Math.random() - 0.5) * 5,
        vy: -1 - Math.random() * 3.5,
        life: 1,
        color, text,
      });
    }
  }

  // 시작 / 재시작
  const start = useCallback(() => {
    if (phase === 'dead' || phase === 'idle') {
      stateRef.current = initState();
      setScore(0); setLives(3); setCombo(0);
      setPhase('playing');
    }
  }, [phase, initState]);

  // 포인터 좌표 → 캔버스 내부 좌표
  const toCanvasXY = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : e;
    const x = (evt.clientX - rect.left) * (W / rect.width);
    const y = (evt.clientY - rect.top)  * (H / rect.height);
    return { x, y };
  };

  // 마우스 / 터치
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let isDown = false;
    const onDown = (e) => {
      e.preventDefault();
      if (phase === 'idle' || phase === 'dead') { start(); return; }
      isDown = true;
      const { x, y } = toCanvasXY(e, canvas);
      stateRef.current.slash.push({ x, y, t: stateRef.current.frame });
    };
    const onMove = (e) => {
      if (!isDown || phase !== 'playing') return;
      e.preventDefault();
      const { x, y } = toCanvasXY(e, canvas);
      const s = stateRef.current;
      s.slash.push({ x, y, t: s.frame });
      if (s.slash.length > SLICE_TAIL) s.slash.shift();
    };
    const onUp = () => {
      isDown = false;
      if (stateRef.current) stateRef.current.slash = [];
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup',   onUp);
    canvas.addEventListener('mouseleave', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove',  onMove, { passive: false });
    canvas.addEventListener('touchend',   onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup',   onUp);
      canvas.removeEventListener('mouseleave', onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchmove',  onMove);
      canvas.removeEventListener('touchend',   onUp);
    };
  }, [phase, start]);

  // 게임 루프
  useEffect(() => {
    if (!stateRef.current) stateRef.current = initState();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let currentPhase = phase;

    function loop() {
      const s = stateRef.current;
      ctx.clearRect(0, 0, W, H);

      // 배경 (분홍·파스텔)
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#fce7f3');
      bg.addColorStop(1, '#fbcfe8');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // 미용실 사인 (좌상단 장식)
      ctx.save();
      ctx.translate(28, 50);
      ctx.font = '28px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('💈', 0, 0);
      ctx.restore();

      if (currentPhase === 'playing') {
        s.frame++;

        // 객체 생성 (난이도 점차 ↑)
        s.spawnTimer++;
        const interval = Math.max(35, 90 - Math.floor(s.frame / 240) * 8);
        if (s.spawnTimer >= interval) {
          s.spawnTimer = 0;
          spawnItem(s);
          // 가끔 2~3개 동시 던짐
          if (s.frame > 600 && Math.random() < 0.25) spawnItem(s);
          if (s.frame > 1200 && Math.random() < 0.18) spawnItem(s);
        }

        // 콤보 타이머 감소
        if (s.comboTimer > 0) {
          s.comboTimer--;
          if (s.comboTimer === 0) { s.combo = 0; setCombo(0); }
        }

        // 객체 물리 + 슬라이스 검사
        for (let i = s.items.length - 1; i >= 0; i--) {
          const it = s.items[i];
          if (!it.sliced) {
            it.vy += GRAV;
            it.x += it.vx;
            it.y += it.vy;
            it.rot += it.vrot;
          } else {
            // 잘린 후엔 빠르게 떨어짐
            it.vy += GRAV * 1.4;
            it.x += it.vx * 1.5;
            it.y += it.vy;
            it.rot += it.vrot * 2;
            it.life = (it.life || 1) - 0.04;
          }

          // 화면 밖 처리
          if (it.y > H + 60 || it.life <= 0) {
            // 머리카락 안 자르고 떨어진 경우만 missed
            if (!it.sliced && it.type === 'good') {
              s.missed++;
              if (s.missed >= 5) {
                currentPhase = 'dead';
                setPhase('dead');
              }
            }
            s.items.splice(i, 1);
            continue;
          }

          // 슬라이스 충돌 (sliced된 건 무시)
          if (it.sliced) continue;
          if (s.slash.length < 2) continue;
          const a = s.slash[s.slash.length - 2];
          const b = s.slash[s.slash.length - 1];
          const d = distPointToSeg(it.x, it.y, a.x, a.y, b.x, b.y);
          if (d < HIT_R) {
            it.sliced = true;
            it.life = 1;
            it.vrot = (Math.random() - 0.5) * 0.4;

            if (it.type === 'good') {
              const gain = 1 + Math.floor(s.combo / 3);
              s.score += gain;
              setScore(s.score);
              s.combo++;
              s.comboTimer = 60;
              setCombo(s.combo);
              spawnParticles(s.particles, it.x, it.y, '#FF6F0F', `+${gain}`);
            } else if (it.type === 'bonus') {
              s.score += 3;
              setScore(s.score);
              s.combo++;
              s.comboTimer = 60;
              setCombo(s.combo);
              spawnParticles(s.particles, it.x, it.y, '#ec4899', '+3');
            } else { // bad
              s.lives--;
              setLives(s.lives);
              s.combo = 0; setCombo(0);
              spawnParticles(s.particles, it.x, it.y, '#52525B', '-1');
              if (s.lives <= 0) {
                currentPhase = 'dead';
                setPhase('dead');
              }
            }
          }
        }

        // 파티클 업데이트
        for (let i = s.particles.length - 1; i >= 0; i--) {
          const p = s.particles[i];
          p.x += p.vx; p.y += p.vy; p.vy += 0.18; p.life -= 0.025;
          if (p.life <= 0) s.particles.splice(i, 1);
        }

        // 게임 오버 시 best 갱신
        if (currentPhase === 'dead') {
          setBest(prev => {
            const next = Math.max(prev, s.score);
            localStorage.setItem(BEST_KEY, String(next));
            return next;
          });
        }
      }

      // 객체 그리기
      s.items.forEach(it => {
        ctx.save();
        if (it.sliced && it.life !== undefined) ctx.globalAlpha = it.life;
        drawEmoji(ctx, it.emoji, it.x, it.y, 40, it.rot);
        ctx.restore();
      });

      // 스와이프 경로 (잔상)
      if (s.slash.length >= 2) {
        ctx.save();
        ctx.lineCap = 'round';
        ctx.strokeStyle = 'rgba(236, 72, 153, 0.7)';
        for (let i = 1; i < s.slash.length; i++) {
          const a = s.slash[i - 1], b = s.slash[i];
          const t = i / s.slash.length;
          ctx.lineWidth = 2 + t * 5;
          ctx.globalAlpha = t;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
        ctx.restore();
      }

      // 파티클
      s.particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.globalAlpha = 1;
      });

      // UI: 점수 (우상단)
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.font = 'bold 30px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(s.score), W - 18, 42);
      ctx.fillStyle = '#9d174d';
      ctx.fillText(String(s.score), W - 20, 40);

      // UI: 라이프 (좌상단, 미용실 사인 옆)
      ctx.font = '20px serif';
      ctx.textAlign = 'left';
      for (let i = 0; i < 3; i++) {
        ctx.globalAlpha = i < s.lives ? 1 : 0.25;
        ctx.fillText('❤️', 60 + i * 24, 50);
      }
      ctx.globalAlpha = 1;

      // UI: 콤보 (가운데)
      if (s.combo >= 2) {
        ctx.fillStyle = '#ec4899';
        ctx.font = 'bold 22px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`콤보 x${s.combo}`, W / 2, 36);
      }

      // UI: missed 카운터 (우상단 점수 아래)
      if (s.missed > 0) {
        ctx.fillStyle = '#9ca3af';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`놓침 ${s.missed}/5`, W - 20, 60);
      }

      // 시작 화면
      if (currentPhase === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('✂️ 셈셈셈!', W / 2, H / 2 - 60);
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#fbcfe8';
        ctx.fillText('손님 모셔!', W / 2, H / 2 - 28);
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#fff';
        ctx.fillText('스와이프해서 머리를 자르세요', W / 2, H / 2 + 14);
        ctx.font = '13px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('💁‍♀️ 머리카락 +1   🌸 꽃 +3   🪮 빗 -1❤️', W / 2, H / 2 + 42);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '12px sans-serif';
        ctx.fillText('탭/클릭으로 시작', W / 2, H / 2 + 76);
      }

      // 게임 오버 카드
      if (currentPhase === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 130, H / 2 - 110, 260, 220, 20);
        ctx.fill();

        ctx.fillStyle = '#9d174d';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('영업 종료 ✂️', W / 2, H / 2 - 65);

        ctx.fillStyle = '#374151';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText('점수', W / 2 - 55, H / 2 - 28);
        ctx.fillText('최고', W / 2 + 55, H / 2 - 28);

        ctx.font = 'bold 36px sans-serif';
        ctx.fillStyle = '#9d174d';
        ctx.fillText(String(s.score), W / 2 - 55, H / 2 + 14);
        ctx.fillStyle = '#ec4899';
        ctx.fillText(String(Math.max(s.score, parseInt(localStorage.getItem(BEST_KEY) || '0'))), W / 2 + 55, H / 2 + 14);

        ctx.fillStyle = '#9d174d';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, H / 2 + 44, 180, 42, 12);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('다시 도전 🔄', W / 2, H / 2 + 70);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, initState]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}
    >
      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: '22px', cursor: 'pointer',
          borderRadius: '8px', padding: '4px 10px', fontWeight: '700',
          backdropFilter: 'blur(4px)',
        }}>
        ✕
      </button>

      {best > 0 && (
        <div style={{ color: '#fbcfe8', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
          🏆 최고 기록: {best}
        </div>
      )}

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'block',
          maxWidth: '95vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          touchAction: 'none',
          cursor: SCISSORS_CURSOR,
        }}
      />

      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
        스와이프로 슬라이스 (마우스 드래그 / 터치)
      </p>
    </div>
  );
}
